import Link from "next/link";
import React, { useState } from "react";
import {
  Collapse,
  FontAwesomeIcon,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  TooltipProps,
  VeBox,
  VeTypography,
  styled,
  tooltipClasses,
  useMediaQuery,
} from "@packages/ui/mui";
import { Capitalize } from "@packages/ui/hoc";
import { faChevronDown, faChevronRight, faChevronUp } from "@packages/ui/libs";
import {
  multiplePermissionCheck,
  userPermissionCheck,
} from "@libs/customHooks/permissionCheck/UserPermissionCheck";
import { userInternalRoleCheck } from "@libs/customHooks/roleCheck/roleCheck";
import { useData } from "@providers/context";
import { VeButton, VeTooltip } from "@packages/ui/components";
import {
  CARBON_INVENTORY_DISABLED_ICON,
  CARBON_INVENTORY_ICON,
  DASHBOARD_ICON,
  DASHBOARD_SELECTED_ICON,
  DEVELOPER_DETAILS_ACTIVE_ICON,
  DEVELOPER_DETAILS_ICON,
  DOCUMENTS_ACTIVE_ICON,
  DOCUMENTS_ICON,
  FINANCIALS_IMPACT_ACTIVE_ICON,
  FINANCIALS_IMPACT_ICON,
  // KYB_ACTIVE_ICON,
  // KYB_ICON,
  PROJECT_DETAILS_ACTIVE_ICON,
  PROJECT_DETAILS_ICON,
  PROJECT_ICON,
  PROJECT_SELECTED_ICON,
  QUESTIONNAIRE_ACTIVE_DISABLED_ICON,
  QUESTIONNAIRE_ACTIVE_ICON,
  QUESTIONNAIRE_ICON,
  PROJECT_DETAILS_DISABLED_ICON,
  DEVELOPER_DETAILS_DISABLED_ICON,
  // KYB_DISABLED_ICON,
  FINANCIALS_IMPACT_DISABLED_ICON,
  DOCUMENTS_DISABLED_ICON,
  RFX_DISABLED_ICON,
  RFX_ICON,
  SETUP_GUIDE_ICON,
  SETUP_GUIDE_SELECTED_ICON,
  TEAMS_SELECTED,
  TEAMS_UNSELECTED,
  // WALLET_ICON,
  // WALLET_SELECTED_ICON,
  MARKETPLACE_ACTIVE_ICON,
  MARKETPLACE_ICON,
  SHARED_WITH_ICON,
  SHARED_WITH_ICON_ACTIVE,
  PREVIEW_PROJECT,
  PREVIEW_PROJECT_DISABLED,
  COLLABORATORS_DISABLED,
  COLLABORATORS,
  COLLABORATORS_SELECTED,
  SETTINGS,
  TEAMS,
  PROFILE_GREY_NEW,
  ACCOUNT_SETTINGS,
  PROFILE_GREY_NEW_SELECTED,
  ACCOUNT_SETTINGS_SELECTED,
  FACILITIES_ICON,
  ACTIVE_FACILITIES_ICON,
  ACTIVE_CHECKLIST_ICON,
  CHECKLIST_ICON,
} from "../../public";
import Image from "next/image";
import { useMutation } from "@tanstack/react-query";
import useQueryData from "@libs/customHooks/useQueryData";
import { mutateAction } from "@libs/actions/mutateAction";
import { toast } from "@libs/helper/toast";
import { useSession } from "next-auth/react";
import useProjectId from "@libs/customHooks/useProjectId";
import SettingsIcon from "@components/SvgComponents/SettingsIcon";

interface MenuItemData {
  text: string;
  path?: any;
  icon?: JSX.Element;
  selectedIcon?: JSX.Element;
  disabledIcon?: JSX.Element;
  subItems?: MenuItemData[];
  permitName?: any;
  internalRole?: any;
  isDisabled?: boolean;
}
//rebuild
const StyledTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }: any) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.white,
    color: "rgba(0, 0, 0, 0.87)",
    boxShadow: theme.shadows[4],
    fontSize: 11,
    marginLeft: "52px",
  },
  "& .MuiTooltip-arrow": {
    color: theme.palette.common.white,
  },
}));

const DrawerData: React.FC<{
  selectedItem: string;
  childSelected: string;
  handleItemClick: (text: string, subItems?: MenuItemData[]) => void;
  setChildSelected: (text: string) => void;
  pathName?: string;
  open: boolean;
  search?: any;
  setSearch?: any;
  handleDrawerOpen?: any;
  openSubItemsAccordionWhenClosed?: any;
  setopenSubItemsAccordionWhenClosed?: any;
  handleClearOpenedSubItemsAccordion?: any;
  handleDrawerClose?: any;
}> = ({
  selectedItem,
  childSelected,
  handleItemClick,
  setChildSelected,
  pathName,
  open,
  search,
  setSearch,
  handleDrawerOpen,
  openSubItemsAccordionWhenClosed,
  setopenSubItemsAccordionWhenClosed,
  handleClearOpenedSubItemsAccordion,
  handleDrawerClose,
}) => {
  const { userData, currentRole, projectPermissionLoading } = useData();
  const { data: session, update } = useSession();
  const projectId = useProjectId();

  const {
    data: previewData,
    isFetching: previewIsFetching,
    refetch,
  } = useQueryData({
    queryKey: ["PREJECT_PREVIEW_DATA", projectId],
    endPoint: `project/share/user/preview-link/${projectId}`,
    enabled: projectId?.length > 0,
  });

  const { mutate, isPending } = useMutation({
    mutationFn: mutateAction,
    mutationKey: ["GENERATE_PREVIEW_LINK"],
    onSuccess: (data: any) => {
      if (data.error)
        throw new Error(data.error?.heading, { cause: data?.error?.message });
      refetch();
      if (data?.link && data?.link?.urlId) {
        window.open(
          `${process.env.NEXT_PUBLIC_VERICAP_FRONTEND_DEV}/project/${data?.link?.urlId}`,
          "_blank",
        );
      } else {
        window.open(
          `${process.env.NEXT_PUBLIC_VERICAP_FRONTEND_DEV}/project/${data?.link}`,
          "_blank",
        );
      }
    },
    onError: (error: any) => {
      toast(error?.cause as string, { type: "error" });
    },
  });

  const handleNewPreviewLink = () => {
    mutate({
      endPoint: `project/share/preview-project/${projectId}`,
      type: "post",
      session,
      update,
    });
  };

  // Get project permissions and global permissions from context
  const { projectPermission, permissionData } = useData();

  // Project-specific permission check function (combines project + global permissions)
  const projectPermissionCheck = (permit: string | undefined): boolean => {
    if (!permit) return false;

    // Get global permissions
    const globalPerms = permissionData?.permissions || [];

    // Get project-specific permissions
    const projectPerms = projectId ? projectPermission?.[projectId] || [] : [];

    // Combine all permissions (global + project-specific + default allowed)
    const combinedPerms = [
      ...globalPerms,
      ...projectPerms,
      "dashboard",
      "wallet",
    ];

    return combinedPerms.includes(permit);
  };

  const multipleProjectPermissionCheck = (permits: any) => {
    if (projectPermissionLoading) return true;
    if (!permits) return false;

    const check = permits?.map((val: string) => projectPermissionCheck(val));
    let result = false;

    for (let i = 0; i < check?.length; i++) {
      result = result || check[i];
      if (result === true) {
        break;
      }
    }

    return result;
  };

  // const VIEW_KYB = userPermissionCheck("kyb.kyb.view");
  const VIEW_FACILITY = projectPermissionCheck("sub-entity.facility.view-list");
  const isMobile = useMediaQuery("(max-width:600px)");
  const menuItemsInvestor: MenuItemData[] = [
    {
      text: "Dashboard",
      path: "/dashboard",
      icon: <Image src={DASHBOARD_ICON} alt="DB_ICON" />,
      selectedIcon: <Image src={DASHBOARD_SELECTED_ICON} alt="DB_ICON" />,
      permitName: ["dashboard"],
      internalRole: ["INVESTOR", "SELLER", "PROJECT_REVIEWER", "SALES_PARTNER"],
    },
    {
      text: "Setup Guide",
      path: "/setup-guide",
      icon: <Image src={SETUP_GUIDE_ICON} alt="DB_ICON" height={17} />,
      selectedIcon: (
        <Image src={SETUP_GUIDE_SELECTED_ICON} alt="DB_ICON" height={17} />
      ),
      permitName: ["setup-guide.setup.access"],
      internalRole: ["INVESTOR", "SELLER", "PROJECT_REVIEWER"],
    },
    {
      text: "My Projects",
      path: "/my-projects",
      icon: <Image src={PROJECT_ICON} alt="VE_ICON" />,
      selectedIcon: <Image src={PROJECT_SELECTED_ICON} alt="DB_ICON" />,
      permitName: ["project.project.view-list", "review-project.project.view"],
      internalRole: ["SELLER", "PROJECT_REVIEWER"],
    },
    // {
    //   text: "Wallet",
    //   path: "/wallet",
    //   icon: <Image src={WALLET_ICON} alt="VE_ICON" />,
    //   selectedIcon: <Image src={WALLET_SELECTED_ICON} alt="DB_ICON" />,
    //   permitName: ["fiat-management.wallet.view"],
    //   internalRole: ["INVESTOR", "SELLER", "PROJECT_REVIEWER"],
    // },
    {
      text: "Teams",
      path: "/teams",
      icon: <Image src={TEAMS_UNSELECTED} alt="VE_ICON" />,
      selectedIcon: <Image src={TEAMS_SELECTED} alt="DB_ICON" />,
      permitName: ["team.users.view"],
      internalRole: ["SELLER", "PROJECT_REVIEWER"],
    },
    {
      text: "Marketplace",
      icon: <Image src={MARKETPLACE_ICON} alt="VE_ICON" />,
      selectedIcon: <Image src={MARKETPLACE_ACTIVE_ICON} alt="DB_ICON" />,
      permitName: [
        "project_catalogue.bundle.view",
        "project_catalogue.manage_links.view_links",
        "project_catalogue.projects.view",
      ],
      // permitName: ["dashboard"],
      internalRole: ["SALES_PARTNER"],
      subItems: [
        {
          text: "Project Catalogue",
          path: `/project-catalogue`,
          // permitName: ["dashboard"],
          permitName: [
            "project_catalogue.projects.view",
            "project_catalogue.bundle.view",
          ],
          internalRole: ["SALES_PARTNER"],
        },
        {
          text: "Manage Links",
          path: `/manage-links`,
          // permitName: ["dashboard"],
          permitName: ["project_catalogue.manage_links.view_links"],
          internalRole: ["SALES_PARTNER"],
        },
      ],
    },
    // {
    //   text: "Credit Analytics",
    //   // isDisabled: !selectedProject?.id,
    //   disabledIcon: (
    //     <Image src={FINANCIALS_IMPACT_DISABLED_ICON} alt="DB_ICON" />
    //   ),
    //   icon: <Image src={FINANCIALS_IMPACT_ICON} alt="VE_ICON" />,
    //   selectedIcon: <Image src={FINANCIALS_IMPACT_ACTIVE_ICON} alt="DB_ICON" />,
    //   permitName: [
    //     "credit-analytics.project.overview",
    //     "credit-analytics.global-carbon-projects.read",
    //     "credit-analytics.carbon-credit-issuances.read",
    //     "credit-analytics.carbon-credit-retirements.read",
    //   ],
    //   internalRole: ["SALES_PARTNER", "SELLER", "INVESTOR", "PROJECT_REVIEWER"],
    //   subItems: [
    //     {
    //       text: "Overview",
    //       path: `/apps/analytics/carbon/overview`,
    //       permitName: ["credit-analytics.project.overview"],
    //       internalRole: [
    //         "SELLER",
    //         "SALES_PARTNER",
    //         "INVESTOR",
    //         "PROJECT_REVIEWER",
    //       ],
    //     },
    //     {
    //       text: "Projects",
    //       path: `/apps/analytics/carbon/projects`,
    //       permitName: ["credit-analytics.global-carbon-projects.read"],
    //       internalRole: [
    //         "SELLER",
    //         "SALES_PARTNER",
    //         "INVESTOR",
    //         "PROJECT_REVIEWER",
    //       ],
    //     },
    //     {
    //       text: "Issued Credits",
    //       path: `/apps/analytics/carbon/issued-credits`,
    //       permitName: ["credit-analytics.carbon-credit-issuances.read"],
    //       internalRole: [
    //         "SELLER",
    //         "SALES_PARTNER",
    //         "INVESTOR",
    //         "PROJECT_REVIEWER",
    //       ],
    //     },
    //     {
    //       text: "Retired Credits",
    //       path: `/apps/analytics/carbon/retirements`,
    //       permitName: ["credit-analytics.carbon-credit-retirements.read"],
    //       internalRole: [
    //         "SELLER",
    //         "SALES_PARTNER",
    //         "INVESTOR",
    //         "PROJECT_REVIEWER",
    //       ],
    //     },
    //     // {
    //     //   text: "Market Activity",
    //     //   path: `/analytics/carbon/market-activity`,
    //     //   permitName: ["project.project.view"],
    //     //   internalRole: ["SELLER"],
    //     // },
    //   ],
    // },
    {
      text: "Shared with me",
      path: "/shared-with-me",
      icon: <Image src={SHARED_WITH_ICON} alt="DB_ICON" />,
      selectedIcon: <Image src={SHARED_WITH_ICON_ACTIVE} alt="DB_ICON" />,
      permitName: ["dashboard"],
      internalRole: ["INVESTOR", "SELLER", "PROJECT_REVIEWER", "SALES_PARTNER"],
    },
  ];
  const menuItemsReviewer: MenuItemData[] = [
    {
      text: "Dashboard",
      path: "/dashboard",
      icon: <Image src={DASHBOARD_ICON} alt="DB_ICON" />,
      selectedIcon: <Image src={DASHBOARD_SELECTED_ICON} alt="DB_ICON" />,
      permitName: ["dashboard"],
      internalRole: ["INVESTOR", "SELLER", "PROJECT_REVIEWER", "SALES_PARTNER"],
    },
    {
      text: "My Projects",
      path: "/my-projects",
      icon: <Image src={PROJECT_ICON} alt="VE_ICON" />,
      selectedIcon: <Image src={PROJECT_SELECTED_ICON} alt="DB_ICON" />,
      permitName: ["project.project.view-list", "review-project.project.view"],
      internalRole: ["SELLER", "PROJECT_REVIEWER"],
    },
    // {
    //   text: "Credit Analytics",
    //   // isDisabled: !selectedProject?.id,
    //   disabledIcon: (
    //     <Image src={FINANCIALS_IMPACT_DISABLED_ICON} alt="DB_ICON" />
    //   ),
    //   icon: <Image src={FINANCIALS_IMPACT_ICON} alt="VE_ICON" />,
    //   selectedIcon: <Image src={FINANCIALS_IMPACT_ACTIVE_ICON} alt="DB_ICON" />,
    //   permitName: [
    //     "credit-analytics.project.overview",
    //     "credit-analytics.global-carbon-projects.read",
    //     "credit-analytics.carbon-credit-issuances.read",
    //     "credit-analytics.carbon-credit-retirements.read",
    //   ],
    //   internalRole: ["SALES_PARTNER", "SELLER", "INVESTOR", "PROJECT_REVIEWER"],
    //   subItems: [
    //     {
    //       text: "Overview",
    //       path: `/apps/analytics/carbon/overview`,
    //       permitName: ["credit-analytics.project.overview"],
    //       internalRole: [
    //         "SELLER",
    //         "SALES_PARTNER",
    //         "INVESTOR",
    //         "PROJECT_REVIEWER",
    //       ],
    //     },
    //     {
    //       text: "Projects",
    //       path: `/apps/analytics/carbon/projects`,
    //       permitName: ["credit-analytics.global-carbon-projects.read"],
    //       internalRole: [
    //         "SELLER",
    //         "SALES_PARTNER",
    //         "INVESTOR",
    //         "PROJECT_REVIEWER",
    //       ],
    //     },
    //     {
    //       text: "Issued Credits",
    //       path: `/apps/analytics/carbon/issued-credits`,
    //       permitName: ["credit-analytics.carbon-credit-issuances.read"],
    //       internalRole: [
    //         "SELLER",
    //         "SALES_PARTNER",
    //         "INVESTOR",
    //         "PROJECT_REVIEWER",
    //       ],
    //     },
    //     {
    //       text: "Retired Credits",
    //       path: `/apps/analytics/carbon/retirements`,
    //       permitName: ["credit-analytics.carbon-credit-retirements.read"],
    //       internalRole: [
    //         "SELLER",
    //         "SALES_PARTNER",
    //         "INVESTOR",
    //         "PROJECT_REVIEWER",
    //       ],
    //     },
    //     // {
    //     //   text: "Market Activity",
    //     //   path: `/analytics/carbon/market-activity`,
    //     //   permitName: ["project.project.view"],
    //     //   internalRole: ["SELLER"],
    //     // },
    //   ],
    // },
    {
      text: "Shared with me",
      path: "/shared-with-me",
      icon: <Image src={SHARED_WITH_ICON} alt="DB_ICON" />,
      selectedIcon: <Image src={SHARED_WITH_ICON_ACTIVE} alt="DB_ICON" />,
      permitName: ["dashboard"],
      internalRole: ["INVESTOR", "SELLER", "PROJECT_REVIEWER", "SALES_PARTNER"],
    },
  ];
  const orgId = userData?.user?.organisationId || "";

  const menuItems = ({
    questionareDisabled = false,
    economicsDisabled = false,
    settings = false,
    apps = false,
    profileTab = false,
  }): MenuItemData[] => {
    return apps
      ? [
          {
            text: "All Projects",
            path: `/apps/all-projects`,
            icon: <Image src={PROJECT_DETAILS_ICON} alt="VE_ICON" />,

            selectedIcon: (
              <Image src={PROJECT_DETAILS_ACTIVE_ICON} alt="DB_ICON" />
            ),
            disabledIcon: (
              <Image src={PROJECT_DETAILS_DISABLED_ICON} alt="DB_ICON" />
            ),
            permitName: ["project.project.view-list"],
            internalRole: ["SELLER"],
          },
          // {
          //   text: "Credit Analytics",
          //   // isDisabled: !selectedProject?.id,
          //   disabledIcon: (
          //     <Image src={FINANCIALS_IMPACT_DISABLED_ICON} alt="DB_ICON" />
          //   ),
          //   icon: <Image src={FINANCIALS_IMPACT_ICON} alt="VE_ICON" />,
          //   selectedIcon: (
          //     <Image src={FINANCIALS_IMPACT_ACTIVE_ICON} alt="DB_ICON" />
          //   ),
          //   permitName: [
          //     "credit-analytics.project.overview",
          //     "credit-analytics.global-carbon-projects.read",
          //     "credit-analytics.carbon-credit-issuances.read",
          //     "credit-analytics.carbon-credit-retirements.read",
          //   ],
          //   internalRole: [
          //     "SALES_PARTNER",
          //     "SELLER",
          //     "INVESTOR",
          //     "PROJECT_REVIEWER",
          //   ],
          //   subItems: [
          //     {
          //       text: "Overview",
          //       path: `/apps/analytics/carbon/overview`,
          //       permitName: ["credit-analytics.project.overview"],
          //       internalRole: [
          //         "SELLER",
          //         "SALES_PARTNER",
          //         "INVESTOR",
          //         "PROJECT_REVIEWER",
          //       ],
          //     },
          //     {
          //       text: "Projects",
          //       path: `/apps/analytics/carbon/projects`,
          //       permitName: ["credit-analytics.global-carbon-projects.read"],
          //       internalRole: [
          //         "SELLER",
          //         "SALES_PARTNER",
          //         "INVESTOR",
          //         "PROJECT_REVIEWER",
          //       ],
          //     },
          //     {
          //       text: "Issued Credits",
          //       path: `/apps/analytics/carbon/issued-credits`,
          //       permitName: ["credit-analytics.carbon-credit-issuances.read"],
          //       internalRole: [
          //         "SELLER",
          //         "SALES_PARTNER",
          //         "INVESTOR",
          //         "PROJECT_REVIEWER",
          //       ],
          //     },
          //     {
          //       text: "Retired Credits",
          //       path: `/apps/analytics/carbon/retirements`,
          //       permitName: ["credit-analytics.carbon-credit-retirements.read"],
          //       internalRole: [
          //         "SELLER",
          //         "SALES_PARTNER",
          //         "INVESTOR",
          //         "PROJECT_REVIEWER",
          //       ],
          //     },
          //     // {
          //     //   text: "Market Activity",
          //     //   path: `/analytics/carbon/market-activity`,
          //     //   permitName: ["project.project.view"],
          //     //   internalRole: ["SELLER"],
          //     // },
          //   ],
          // },
          {
            text: "Shared with me",
            path: "/shared-with-me",
            icon: <Image src={SHARED_WITH_ICON} alt="DB_ICON" />,
            selectedIcon: <Image src={SHARED_WITH_ICON_ACTIVE} alt="DB_ICON" />,
            permitName: ["dashboard"],
            internalRole: [
              "INVESTOR",
              "SELLER",
              "PROJECT_REVIEWER",
              "SALES_PARTNER",
            ],
          },
        ]
      : settings
        ? [
            {
              text: "User Management",
              icon: <Image src={TEAMS} alt="VE_ICON" />,
              selectedIcon: <Image src={TEAMS} alt="DB_ICON" />,
              disabledIcon: <Image src={TEAMS} alt="DB_ICON" />,
              permitName: ["team.users.view", "team.roles.view"],
              internalRole: [
                "INVESTOR",
                "SELLER",
                "PROJECT_REVIEWER",
                "SALES_PARTNER",
              ],
              subItems: [
                {
                  text: "Users",
                  path: `/admin/${orgId}/users`,
                  permitName: ["team.users.view"],
                  internalRole: [
                    "INVESTOR",
                    "SELLER",
                    "PROJECT_REVIEWER",
                    "SALES_PARTNER",
                  ],
                },
                {
                  text: "Roles",
                  path: `/admin/${orgId}/roles`,
                  permitName: ["team.roles.view"],
                  internalRole: [
                    "INVESTOR",
                    "SELLER",
                    "PROJECT_REVIEWER",
                    "SALES_PARTNER",
                  ],
                },
              ],
            },
            {
              text: "Organisation Settings",
              icon: <Image src={SETTINGS} alt="VE_ICON" />,
              selectedIcon: <Image src={SETTINGS} alt="DB_ICON" />,
              disabledIcon: <Image src={SETTINGS} alt="DB_ICON" />,
              permitName: ["dashboard"],
              internalRole: [
                "INVESTOR",
                "SELLER",
                "PROJECT_REVIEWER",
                "SALES_PARTNER",
              ],
              subItems: [
                {
                  text: "Overview",
                  path: `/admin/${orgId}/overview`,
                  permitName: ["dashboard"],
                  internalRole: [
                    "INVESTOR",
                    "SELLER",
                    "PROJECT_REVIEWER",
                    "SALES_PARTNER",
                  ],
                },
                {
                  text: "Business Profile",
                  path: `/admin/${orgId}/profile`,
                  permitName: ["dashboard"],
                  internalRole: [
                    "INVESTOR",
                    "SELLER",
                    "PROJECT_REVIEWER",
                    "SALES_PARTNER",
                  ],
                },
                {
                  text: "Domains",
                  path: `/admin/${orgId}/domains`,
                  permitName: ["customers.domains.view"],
                  internalRole: [
                    "INVESTOR",
                    "SELLER",
                    "PROJECT_REVIEWER",
                    "SALES_PARTNER",
                  ],
                },
              ],
            },
          ]
        : profileTab
          ? [
              {
                text: "Profile",
                path: `/profile`,
                icon: <Image src={PROFILE_GREY_NEW} alt="DB_ICON" />,
                selectedIcon: (
                  <Image src={PROFILE_GREY_NEW_SELECTED} alt="DB_ICON" />
                ),
                permitName: ["dashboard"],
                internalRole: [
                  "INVESTOR",
                  "SELLER",
                  "PROJECT_REVIEWER",
                  "SALES_PARTNER",
                ],
              },
              {
                text: "Account Settings",
                path: `/settings/account`,
                icon: <Image src={ACCOUNT_SETTINGS} alt="DB_ICON" />,
                selectedIcon: (
                  <Image src={ACCOUNT_SETTINGS_SELECTED} alt="DB_ICON" />
                ),
                permitName: ["dashboard"],
                internalRole: [
                  "INVESTOR",
                  "SELLER",
                  "PROJECT_REVIEWER",
                  "SALES_PARTNER",
                ],
              },
              {
                text: "General Settings",
                path: `/settings/general`,
                icon: <SettingsIcon />,
                selectedIcon: <SettingsIcon active />,

                permitName: ["dashboard"],
                internalRole: [
                  "INVESTOR",
                  "SELLER",
                  "PROJECT_REVIEWER",
                  "SALES_PARTNER",
                ],
              },
            ]
          : currentRole === "INVESTOR" || currentRole === "SALES_PARTNER"
            ? menuItemsInvestor
            : currentRole === "PROJECT_REVIEWER"
              ? menuItemsReviewer
              : [
                  {
                    text: "Overview",
                    path: `/manage-project/${projectId}/overview`,
                    icon: <Image src={DASHBOARD_ICON} alt="DB_ICON" />,
                    selectedIcon: (
                      <Image src={DASHBOARD_SELECTED_ICON} alt="DB_ICON" />
                    ),
                    permitName: [
                      "project-details.basic-info.view",
                      "onboarding.general-questionnaire.view",
                      "onboarding.financing-questionnaire.view",
                      "onboarding.cashflow.view",
                      "onboarding.carbon schedule.view",
                      "onboarding.stakeholders.view",
                      "onboarding.team-members.view",
                      "kyb.kyb.view",
                      "setup-guide.setup.access",
                    ],
                    internalRole: ["SELLER"],
                    isDisabled: projectPermissionLoading,
                  },
                  {
                    text: "Project Details",
                    icon: <Image src={PROJECT_DETAILS_ICON} alt="VE_ICON" />,
                    selectedIcon: (
                      <Image src={PROJECT_DETAILS_ACTIVE_ICON} alt="DB_ICON" />
                    ),
                    disabledIcon: (
                      <Image
                        src={PROJECT_DETAILS_DISABLED_ICON}
                        alt="DB_ICON"
                      />
                    ),
                    permitName: [
                      "project-details.basic-info.view",
                      "files.gallery.view",
                    ],
                    internalRole: ["SELLER"],
                    isDisabled:
                      !projectId ||
                      projectPermissionLoading ||
                      !projectDetails?.commodityId,
                    subItems:
                      projectDetails?.commodityId ===
                      Number(process.env.NEXT_PUBLIC_USTAX_COMMODITY_ID)
                        ? [
                            {
                              text: "Basic Details",
                              path: `/manage-project/${projectId}/basic-details`,
                              permitName: ["project-details.basic-info.view"],
                              internalRole: ["SELLER"],
                            },
                            {
                              text: "Gallery",
                              path: `/manage-project/${projectId}/gallery`,
                              permitName: ["files.gallery.view"],
                              internalRole: ["SELLER"],
                            },
                          ]
                        : [
                            {
                              text: "Basic Details",
                              path: `/manage-project/${projectId}/basic-details`,
                              permitName: ["project-details.basic-info.view"],
                              internalRole: ["SELLER"],
                            },
                            {
                              text: "Gallery",
                              path: `/manage-project/${projectId}/gallery`,
                              permitName: ["files.gallery.view"],
                              internalRole: ["SELLER"],
                            },
                            {
                              text: "Impact",
                              path: `/manage-project/${projectId}/impact`,
                              permitName: ["project-details.basic-info.view"],
                              internalRole: ["SELLER"],
                            },
                          ],
                  },
                  {
                    text: "Documents",
                    isDisabled:
                      !projectId ||
                      projectPermissionLoading ||
                      !projectDetails?.commodityId,
                    disabledIcon: (
                      <Image src={DOCUMENTS_DISABLED_ICON} alt="VE_ICON" />
                    ),
                    icon: <Image src={DOCUMENTS_ICON} alt="VE_ICON" />,
                    selectedIcon: (
                      <Image src={DOCUMENTS_ACTIVE_ICON} alt="DB_ICON" />
                    ),
                    permitName: [
                      "files.documents.view",
                      "files.data-rooms.view",
                    ],
                    internalRole: ["SELLER"],
                    subItems: [
                      {
                        text: "Files",
                        path: `/manage-project/${projectId}/documents`,
                        permitName: ["files.documents.view"],
                        internalRole: ["SELLER"],
                      },
                      {
                        text: "Data Room",
                        path: `/manage-project/${projectId}/data-room`,
                        permitName: ["files.data-rooms.view"],
                        internalRole: ["SELLER"],
                      },
                    ],
                  },
                  ...(VIEW_FACILITY &&
                  projectDetails?.commodityId ===
                    Number(process.env.NEXT_PUBLIC_USTAX_COMMODITY_ID)
                    ? [
                        {
                          text: "Facilities",
                          disabledIcon: (
                            <Image src={PROJECT_ICON} alt="VE_ICON" />
                          ),
                          path: `/manage-project/${projectId}/facilities`,
                          icon: <Image src={FACILITIES_ICON} alt="VE_ICON" />,
                          selectedIcon: (
                            <Image src={ACTIVE_FACILITIES_ICON} alt="DB_ICON" />
                          ),
                          permitName: ["management.tasks.view"],
                          internalRole: ["SELLER"],
                          isDisabled: !projectDetails?.commodityId,
                        },
                      ]
                    : []),
                  ...(projectDetails?.commodityId ===
                  Number(process.env.NEXT_PUBLIC_USTAX_COMMODITY_ID)
                    ? [
                        {
                          text: "Checklist",
                          disabledIcon: (
                            <Image src={PROJECT_ICON} alt="VE_ICON" />
                          ),
                          path: `/manage-project/${projectId}/checklist`,
                          icon: <Image src={CHECKLIST_ICON} alt="VE_ICON" />,
                          selectedIcon: (
                            <Image src={ACTIVE_CHECKLIST_ICON} alt="DB_ICON" />
                          ),
                          permitName: ["checklist.checklist-details.view-list"],
                          internalRole: ["SELLER"],
                          isDisabled: !projectDetails?.commodityId,
                        },
                      ]
                    : []),
                  ...(projectDetails?.commodityId !==
                  Number(process.env.NEXT_PUBLIC_USTAX_COMMODITY_ID)
                    ? [
                        {
                          text: "Questionnaire",
                          icon: (
                            <Image src={QUESTIONNAIRE_ICON} alt="VE_ICON" />
                          ),
                          selectedIcon: (
                            <Image
                              src={QUESTIONNAIRE_ACTIVE_ICON}
                              alt="DB_ICON"
                            />
                          ),
                          permitName: [
                            "onboarding.general-questionnaire.view",
                            "onboarding.financing-questionnaire.view",
                          ],
                          internalRole: ["SELLER"],
                          disabledIcon: (
                            <Image
                              src={QUESTIONNAIRE_ACTIVE_DISABLED_ICON}
                              alt="DB_ICON"
                            />
                          ),
                          isDisabled:
                            projectDetails?.commodityId ===
                            Number(process.env.NEXT_PUBLIC_USTAX_COMMODITY_ID)
                              ? questionareDisabled ||
                                projectPermissionLoading ||
                                !projectDetails?.commodityId
                              : (questionareDisabled && economicsDisabled) ||
                                projectPermissionLoading ||
                                !projectDetails?.commodityId,
                          ...(projectDetails?.commodityId ===
                          Number(process.env.NEXT_PUBLIC_USTAX_COMMODITY_ID)
                            ? {
                                path: `/manage-project/${projectId}/due-dilligence`,
                              }
                            : questionareDisabled && economicsDisabled
                              ? {
                                  path: `/manage-project/${projectId}/due-dilligence`,
                                }
                              : {
                                  subItems: [
                                    ...(questionareDisabled
                                      ? []
                                      : [
                                          {
                                            text: "Due diligence",
                                            path: `/manage-project/${projectId}/due-dilligence`,
                                            permitName: [
                                              "onboarding.general-questionnaire.view",
                                            ],
                                            internalRole: ["SELLER"],
                                          },
                                        ]),
                                    ...(economicsDisabled
                                      ? []
                                      : [
                                          {
                                            text: "Economics & Delivery",
                                            path: `/manage-project/${projectId}/economics-delivery`,
                                            permitName: [
                                              "onboarding.financing-questionnaire.view",
                                            ],
                                            internalRole: ["SELLER"],
                                          },
                                        ]),
                                  ],
                                }),
                        },
                      ]
                    : []),
                  ...(projectDetails?.commodityId ===
                  Number(process.env.NEXT_PUBLIC_USTAX_COMMODITY_ID)
                    ? []
                    : [
                        {
                          text: "Financials & Impact",
                          isDisabled:
                            !projectId ||
                            projectPermissionLoading ||
                            !projectDetails?.commodityId,
                          disabledIcon: (
                            <Image
                              src={FINANCIALS_IMPACT_DISABLED_ICON}
                              alt="DB_ICON"
                            />
                          ),
                          icon: (
                            <Image src={FINANCIALS_IMPACT_ICON} alt="VE_ICON" />
                          ),
                          selectedIcon: (
                            <Image
                              src={FINANCIALS_IMPACT_ACTIVE_ICON}
                              alt="DB_ICON"
                            />
                          ),
                          permitName: [
                            "onboarding.cashflow.view",
                            "onboarding.carbon schedule.view",
                          ],
                          internalRole: ["SELLER"],
                          ...(projectId
                            ? {
                                subItems: [
                                  {
                                    text: "Cashflow",
                                    path: `/manage-project/${projectId}/cashflow`,
                                    permitName: ["onboarding.cashflow.view"],
                                    internalRole: ["SELLER"],
                                  },
                                  {
                                    text: "Carbon Schedule",
                                    path: `/manage-project/${projectId}/carbon-schedule`,
                                    permitName: [
                                      "onboarding.carbon schedule.view",
                                    ],
                                    internalRole: ["SELLER"],
                                  },
                                ],
                              }
                            : {
                                path: `/manage-project/${projectId}/cashflow`,
                              }),
                        },
                        {
                          text: "Developer Details",
                          isDisabled:
                            !projectId ||
                            projectPermissionLoading ||
                            !projectDetails?.commodityId,
                          icon: (
                            <Image src={DEVELOPER_DETAILS_ICON} alt="VE_ICON" />
                          ),
                          selectedIcon: (
                            <Image
                              src={DEVELOPER_DETAILS_ACTIVE_ICON}
                              alt="DB_ICON"
                            />
                          ),
                          disabledIcon: (
                            <Image
                              src={DEVELOPER_DETAILS_DISABLED_ICON}
                              alt="DB_ICON"
                            />
                          ),
                          permitName: [
                            "onboarding.stakeholders.view",
                            "onboarding.team-members.view",
                          ],
                          internalRole: ["SELLER"],
                          subItems: [
                            {
                              text: "Stakeholders",
                              path: `/manage-project/${projectId}/stakeholders`,
                              permitName: ["onboarding.stakeholders.view"],
                              internalRole: ["SELLER"],
                            },
                            {
                              text: "Team Members",
                              path: `/manage-project/${projectId}/team-profiles`,
                              permitName: ["onboarding.team-members.view"],
                              internalRole: ["SELLER"],
                            },
                          ],
                        },
                      ]),
                  // ...(VIEW_KYB &&
                  // projectDetails?.commodityId !==
                  //   Number(process.env.NEXT_PUBLIC_USTAX_COMMODITY_ID)
                  //   ? [
                  //       {
                  //         text: "Know Your Business",
                  //         isDisabled:
                  //           !projectId ||
                  //           projectPermissionLoading ||
                  //           !projectDetails?.commodityId,
                  //         disabledIcon: (
                  //           <Image src={KYB_DISABLED_ICON} alt="DB_ICON" />
                  //         ),
                  //         path: `/manage-project/${projectId}/know-your-business`,
                  //         icon: <Image src={KYB_ICON} alt="VE_ICON" />,
                  //         selectedIcon: (
                  //           <Image src={KYB_ACTIVE_ICON} alt="DB_ICON" />
                  //         ),
                  //         permitName: ["kyb.kyb.view"],
                  //         internalRole: ["SELLER"],
                  //       },
                  //     ]
                  //   : []),
                  {
                    text: "Tasks",
                    isDisabled:
                      !projectId ||
                      projectPermissionLoading ||
                      !projectDetails?.commodityId,
                    disabledIcon: <Image src={PROJECT_ICON} alt="VE_ICON" />,
                    path: `/manage-project/${projectId}/tasks`,
                    icon: <Image src={PROJECT_ICON} alt="VE_ICON" />,
                    selectedIcon: (
                      <Image src={PROJECT_SELECTED_ICON} alt="DB_ICON" />
                    ),
                    permitName: ["management.tasks.view"],
                    internalRole: ["SELLER"],
                  },
                  {
                    text: "Collaborators",
                    isDisabled:
                      !projectId ||
                      projectPermissionLoading ||
                      !projectDetails?.commodityId,
                    disabledIcon: (
                      <Image src={COLLABORATORS_DISABLED} alt="VE_ICON" />
                    ),
                    path: `/manage-project/${projectId}/collaborators`,
                    icon: <Image src={COLLABORATORS} alt="VE_ICON" />,
                    selectedIcon: (
                      <Image src={COLLABORATORS_SELECTED} alt="DB_ICON" />
                    ),
                    permitName: ["management.collaborators.view-list"],
                    internalRole: ["SELLER"],
                  },
                  ...(projectDetails?.commodityId ===
                  Number(process.env.NEXT_PUBLIC_USTAX_COMMODITY_ID)
                    ? []
                    : [
                        {
                          text: "Carbon Inventory",
                          isDisabled: true,
                          path: "/carbon-inventory",
                          icon: (
                            <Image src={CARBON_INVENTORY_ICON} alt="VE_ICON" />
                          ),
                          selectedIcon: (
                            <Image src={CARBON_INVENTORY_ICON} alt="DB_ICON" />
                          ),
                          disabledIcon: (
                            <Image
                              src={CARBON_INVENTORY_DISABLED_ICON}
                              alt="DB_ICON"
                            />
                          ),
                          permitName: ["project.project.view"],
                          internalRole: ["SELLER"],
                        },
                        {
                          text: "RFX",
                          isDisabled: true,
                          path: "/rfx",
                          icon: <Image src={RFX_ICON} alt="VE_ICON" />,
                          selectedIcon: <Image src={RFX_ICON} alt="DB_ICON" />,
                          disabledIcon: (
                            <Image src={RFX_DISABLED_ICON} alt="DB_ICON" />
                          ),
                          permitName: ["project.project.view"],
                          internalRole: ["SELLER"],
                        },
                      ]),
                ];
  };

  const userWalletCreated =
    userData?.user?.fiatWalletCreated === true ? true : false;
  // const queryClient = useQueryClient();
  // const projectData: any = queryClient.getQueryData([
  //   "project-details",
  //   projectId,
  // ]);
  const canViewProject = projectPermissionCheck(
    "project-details.basic-info.view",
  );
  const { data } = useQueryData({
    queryKey: ["project-details", projectId, canViewProject],
    endPoint: `/project/v2/onboarding/${projectId}`,
    enabled: projectId?.length > 1 && canViewProject,
  });
  const projectDetails = data?.project;
  const questionareDisabled = !projectDetails?.formData?.DUE_DILIGENCE;
  const economicsDisabled = !projectDetails?.formData?.ECONOMIC_DELIVERY;
  // Check if pathname contains /admin/ to determine settings mode
  const isAdminRoute = pathName?.includes("/admin/");
  const isAppRoute =
    currentRole === "SELLER" &&
    (pathName?.includes("/shared-with-me") ||
      pathName?.includes("/apps/") ||
      pathName?.includes("/project/") ||
      pathName?.includes("/link/") ||
      pathName?.includes("/bundles"));
  const isProfileTab =
    pathName?.includes("/profile") || pathName?.includes("/settings/");

  let menuItemsArray = menuItems({
    questionareDisabled,
    economicsDisabled,
    settings: isAdminRoute,
    apps: isAppRoute,
    profileTab: isProfileTab,
  });

  return (
    <VeBox
      sx={{
        width: "100%",
        height: "100%",
        position: "relative",
        backgroundColor: isAdminRoute ? "#F5F4FA" : "transparent",
      }}
    >
      {
        // Project developer code
        <List
          sx={{
            "&.MuiList-root": {
              padding: 0,
            },
            overflowY: "auto",
            scrollbarWidth: "none",
            position: "absolute",
            inset: "0px",
            // width: "100%",
            // NOTE: REVERT IS SOMETHING BREAKS IN THE NAV-BAR
            // height: "auto",
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
          className="drawer-data"
        >
          {menuItemsArray?.filter((item) => {
            const mainMatch = item?.text
              ?.toLowerCase()
              .includes(search?.toLowerCase());
            const subMatch = item?.subItems?.some(
              (sub) => sub?.text?.toLowerCase().includes(search?.toLowerCase()),
            );
            return mainMatch || subMatch;
          })?.length > 0 ? (
            menuItemsArray
              ?.filter((item) => {
                const mainMatch = item?.text
                  ?.toLowerCase()
                  .includes(search?.toLowerCase());
                const subMatch = item?.subItems?.some(
                  (sub) =>
                    sub?.text?.toLowerCase().includes(search?.toLowerCase()),
                );
                return mainMatch || subMatch;
              })
              .map((item) => {
                const filter =
                  multipleProjectPermissionCheck(item?.permitName) &&
                  userInternalRoleCheck(item?.internalRole) &&
                  (item.path === "/setup-guide"
                    ? !userData?.isDashboard
                    : true);
                return (
                  <VeTooltip
                    placement={open ? "top" : "right"}
                    title={
                      item?.isDisabled ? (
                        <VeTypography
                          sx={{
                            padding: "4px 8px",
                            display: "flex",
                            alignItems: "center",
                          }}
                          variant="label3White"
                        >
                          Coming Soon
                        </VeTypography>
                      ) : (
                        ""
                      )
                    }
                    arrow
                    color="#1D2330"
                    sx={{
                      padding: filter ? "4px 8px !important" : "none",
                    }}
                  >
                    <VeBox key={item.text}>
                      {item.path && filter ? (
                        <VeTooltip
                          title={
                            !open &&
                            !isMobile && (
                              <VeTypography
                                sx={{
                                  padding: "4px 8px",
                                  display: "flex",
                                  alignItems: "center",
                                }}
                                variant="label3White"
                              >
                                {item.text}
                              </VeTypography>
                            )
                          }
                          arrow
                          placement="right"
                          color="#1D2330"
                        >
                          <Link
                            href={item?.isDisabled ? "#" : item.path || "#"}
                            onClick={(e) => {
                              if (item?.isDisabled) {
                                e.preventDefault(); // prevent routing
                              }
                            }}
                            style={{
                              pointerEvents: item?.isDisabled ? "none" : "auto",
                              opacity: item?.isDisabled ? 0.5 : 1,
                            }}
                          >
                            <ListItem
                              className="list-item"
                              disablePadding
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                backgroundColor:
                                  pathName === item.path
                                    ? "#F9F7FD"
                                    : "transparent",
                                "& .Mui-selected": {
                                  backgroundColor: "transparent !important",
                                },
                                "& .MuiListItemButton-root": {
                                  minHeight: "36px !important",
                                  paddingY: 0,
                                  padding: open
                                    ? "0px 0px 0px 16px"
                                    : pathName === item.path
                                      ? "0px 0px 0px 3px"
                                      : "0",
                                },
                                "&:hover": {
                                  borderRadius: "4px !important",
                                },
                              }}
                            >
                              <ListItemButton
                                className="list-item-button"
                                sx={{
                                  minHeight: 48,
                                  justifyContent: open ? "initial" : "center",
                                  px: 2.5,
                                  backgroundColor:
                                    pathName === item.path
                                      ? "#F9F7FD"
                                      : "transparent",
                                  borderRadius: "4px",
                                  position: "relative",
                                  // 2. Conditionally add styles for the ::before pseudo-element
                                  ...(pathName === item.path
                                    ? {
                                        // Target the ::before pseudo-element of *this* component
                                        "&::before": {
                                          content: '""', // Required for pseudo-elements
                                          position: "absolute", // Position relative to the container (due to position: 'relative' above)
                                          top: "10%", // Adjust these percentages for vertical spacing
                                          bottom: "10%", // (10% top space + 80% line + 10% bottom space = 100%)
                                          left: 0, // Position at the left edge
                                          width: "2px", // The thickness of your line
                                          backgroundColor: "#703DDE", // The color of your line
                                          // Optional: Ensure it doesn't interfere with content layout or events
                                          borderRadius: "4px",
                                          pointerEvents: "none",
                                        },
                                      }
                                    : {}),
                                  ":hover": {
                                    borderRadius: "4px",
                                  },
                                  "& .Mui-selected": {
                                    backgroundColor: "transparent !important",
                                  },
                                }}
                                onClick={() => {
                                  handleItemClick(item.text, item.subItems);
                                  if (handleClearOpenedSubItemsAccordion) {
                                    handleClearOpenedSubItemsAccordion();
                                  }
                                  if (handleDrawerClose) {
                                    handleDrawerClose();
                                  }
                                }}
                                selected={
                                  selectedItem === item.text && !childSelected
                                }
                              >
                                <VeBox
                                  width="85%"
                                  display="flex"
                                  justifyContent="center"
                                  alignItems="center"
                                >
                                  <VeBox
                                    width="28px"
                                    display="flex"
                                    alignItems="center"
                                    justifyContent={
                                      !open ? "center" : "flex-start"
                                    }
                                  >
                                    <ListItemIcon
                                      className="list-item-icon"
                                      sx={{
                                        minWidth: 0,
                                        mr: open ? 3 : "none",
                                        justifyContent: "center",
                                      }}
                                    >
                                      {item?.isDisabled
                                        ? item?.disabledIcon
                                        : pathName === item.path ||
                                            pathName?.includes(item.path)
                                          ? item.selectedIcon
                                          : item.icon}
                                    </ListItemIcon>
                                  </VeBox>
                                  {open && (
                                    <ListItemText className="list-item-text">
                                      <VeTypography
                                        variant={
                                          item?.isDisabled
                                            ? "label2Disabled"
                                            : pathName === item.path ||
                                                pathName?.includes(item.path)
                                              ? "label2StrongBrand"
                                              : "label2Main"
                                        }
                                      >
                                        {item.text}
                                      </VeTypography>
                                    </ListItemText>
                                  )}
                                </VeBox>
                              </ListItemButton>
                            </ListItem>
                          </Link>
                        </VeTooltip>
                      ) : !open && filter ? (
                        <VeTooltip
                          title={
                            !isMobile && (
                              <VeTypography
                                sx={{
                                  padding: "4px 8px",
                                  display: "flex",
                                  alignItems: "center",
                                }}
                                variant="label3White"
                              >
                                {item.text}
                              </VeTypography>
                            )
                          }
                          arrow
                          placement="right"
                          color="#1D2330"
                        >
                          <span>
                            {" "}
                            <ListItem
                              disablePadding
                              sx={{
                                backgroundColor: item.subItems?.some(
                                  (subItem: any) => subItem.path === pathName,
                                )
                                  ? "#F9F7FD"
                                  : "transparent",
                                "& .MuiListItemButton-root": {
                                  padding: "8px 16px",
                                  minHeight: "36px !important",
                                },
                                "&:hover": {
                                  borderRadius: "4px",
                                },
                              }}
                            >
                              <ListItemButton
                                sx={{
                                  minHeight: 48,
                                  justifyContent: "center",
                                  px: 2.5,
                                  paddingRight: "26px",
                                  borderRadius: "4px",
                                  backgroundColor: item.subItems?.some(
                                    (subItem: any) => subItem.path === pathName,
                                  )
                                    ? "#F9F7FD"
                                    : "transparent",
                                  position: "relative",

                                  // 2. Conditionally add styles for the ::before pseudo-element
                                  ...(item.subItems?.some(
                                    (subItem: any) => subItem.path === pathName,
                                  )
                                    ? {
                                        // Target the ::before pseudo-element of *this* component
                                        "&::before": {
                                          content: '""', // Required for pseudo-elements
                                          position: "absolute", // Position relative to the container (due to position: 'relative' above)
                                          top: "10%", // Adjust these percentages for vertical spacing
                                          bottom: "10%", // (10% top space + 80% line + 10% bottom space = 100%)
                                          left: 0, // Position at the left edge
                                          width: "2px", // The thickness of your line
                                          backgroundColor: "#703DDE", // The color of your line
                                          // Optional: Ensure it doesn't interfere with content layout or events
                                          borderRadius: "4px",
                                          pointerEvents: "none",
                                        },
                                      }
                                    : {}),
                                }}
                                selected={item.subItems?.some(
                                  (subItem: any) => subItem.path === pathName,
                                )}
                              >
                                <ListItemIcon
                                  sx={{
                                    minWidth: 0,
                                    mr: open ? 3 : "auto",
                                    justifyContent: "center",
                                  }}
                                  onClick={() => {
                                    if (item?.subItems?.length! > 0) {
                                      handleDrawerOpen();
                                      setopenSubItemsAccordionWhenClosed(
                                        item.text,
                                      );
                                    }
                                  }}
                                >
                                  {item.subItems?.some(
                                    (subItem: any) => subItem.path === pathName,
                                  )
                                    ? item.selectedIcon
                                    : item.icon}
                                </ListItemIcon>
                              </ListItemButton>
                            </ListItem>
                          </span>
                        </VeTooltip>
                      ) : (
                        item?.subItems?.length &&
                        filter && (
                          <SubItemsComponent
                            item={item}
                            pathName={pathName}
                            selectedItem={selectedItem}
                            childSelected={childSelected}
                            open={open}
                            handleItemClick={handleItemClick}
                            search={search}
                            setChildSelected={setChildSelected}
                            setSearch={setSearch}
                            handleDrawerOpen={handleDrawerOpen}
                            openSubItemsAccordionWhenClosed={
                              openSubItemsAccordionWhenClosed
                            }
                            handleClearOpenedSubItemsAccordion={
                              handleClearOpenedSubItemsAccordion
                            }
                            handleDrawerClose={handleDrawerClose}
                            permissionCheckFn={multipleProjectPermissionCheck}
                          />
                        )
                      )}
                    </VeBox>
                  </VeTooltip>
                );
              })
          ) : (
            <VeBox
              display={"flex"}
              alignItems={"center"}
              justifyContent={"center"}
            >
              <VeTypography variant="label3Tertiary" padding="8px 16px">
                No results found.
              </VeTypography>
            </VeBox>
          )}
          {userInternalRoleCheck(["SELLER"]) && (
            <VeTooltip
              placement={open ? "top" : "right"}
              title={
                !previewData?.previewLink?.isPreview ? (
                  <VeTypography
                    sx={{
                      padding: "4px 8px",
                      display: "flex",
                      alignItems: "center",
                      maxWidth: "150px",
                    }}
                    variant="label3White"
                  >
                    You can preview this project once it has been enabled by
                    Admin
                  </VeTypography>
                ) : null
              }
              arrow
              color="#1D2330"
              sx={{
                padding: "4px 8px !important",
                marginTop: "auto",
                marginBottom: "24px",
              }}
            >
              {projectId && (
                <VeBox
                  display={"flex"}
                  alignItems={"center"}
                  justifyContent={"center"}
                >
                  {open ? (
                    <VeButton
                      buttonClick={() => {
                        if (previewData?.previewLink.link) {
                          window.open(
                            `${process.env.NEXT_PUBLIC_VERICAP_FRONTEND_DEV}/project/${previewData?.previewLink.link}`,
                            "_blank",
                          );
                        } else {
                          handleNewPreviewLink();
                        }
                      }}
                      disabled={
                        previewIsFetching ||
                        !previewData?.previewLink?.isPreview
                      }
                      buttonVariant="outlined"
                      loading={isPending}
                    >
                      {
                        <ListItemIcon
                          className="list-item-icon"
                          sx={{
                            minWidth: 0,
                            mr: 2,
                            justifyContent: "center",
                          }}
                        >
                          <Image
                            src={
                              previewIsFetching ||
                              !previewData?.previewLink?.isPreview
                                ? PREVIEW_PROJECT_DISABLED
                                : PREVIEW_PROJECT
                            }
                            alt="PREVIEW_PROJECT"
                            style={{
                              filter:
                                previewIsFetching ||
                                !previewData?.previewLink?.isPreview
                                  ? "grayscale(100%)"
                                  : "none",
                            }}
                          />
                        </ListItemIcon>
                      }
                      Preview project
                    </VeButton>
                  ) : (
                    <VeBox
                      sx={{
                        cursor: "pointer",
                      }}
                      onClick={() => {
                        if (
                          previewIsFetching ||
                          !previewData?.previewLink?.isPreview
                        ) {
                          return;
                        }
                        window.open(
                          `${process.env.NEXT_PUBLIC_VERICAP_FRONTEND_DEV}/project/${previewData?.previewLink.link}`,
                          "_blank",
                        );
                      }}
                    >
                      <Image
                        src={
                          previewIsFetching ||
                          !previewData?.previewLink?.isPreview
                            ? PREVIEW_PROJECT_DISABLED
                            : PREVIEW_PROJECT
                        }
                        alt="PREVIEW_PROJECT"
                        style={{
                          filter:
                            previewIsFetching ||
                            !previewData?.previewLink?.isPreview
                              ? "grayscale(100%)"
                              : "none",
                        }}
                      />
                    </VeBox>
                  )}
                </VeBox>
              )}
            </VeTooltip>
          )}
        </List>
      }
    </VeBox>
  );
};

export default DrawerData;

export const SubItemsComponent = ({
  item,
  pathName,
  selectedItem,
  childSelected,
  open,
  handleItemClick,
  search,
  setChildSelected,
  setSearch,
  openSubItemsAccordionWhenClosed,
  handleClearOpenedSubItemsAccordion,
  handleDrawerClose,
  permissionCheckFn,
}: any) => {
  const isSubItemActive = React.useMemo(
    () => item.subItems?.some((ele: any) => ele.path === pathName),
    [item.subItems, pathName, search],
  );

  const isSearchMatch = React.useMemo(
    () =>
      search?.length > 0 &&
      (item.text?.toLowerCase().includes(search.toLowerCase()) ||
        item.subItems?.some(
          (ele: any) => ele.text?.toLowerCase().includes(search.toLowerCase()),
        )),
    [item, search],
  );

  // To get accurate state if the accordion is open or closed
  const [accordionOpen, setAccordionOpen] = React.useState(
    selectedItem === item.text || isSearchMatch || isSubItemActive || false,
  );

  const isTab = useMediaQuery("(max-width: 900px)");

  return (
    <ListItem
      disablePadding
      sx={{
        display: "block",
        backgroundColor:
          isSubItemActive && !accordionOpen ? "#F9F7FD" : "transparent",
        "& .Mui-selected": {
          backgroundColor: "transparent !important",
        },
        "& .MuiListItemButton-root": {
          padding: open ? "0px 0px 0px 16px" : "0px 16px 0px 16px",
          minHeight: "36px",
          paddingY: 0,
        },
        borderRadius: "4px",
      }}
    >
      <ListItemButton
        sx={{
          gap: isTab ? "24px" : "12px",
          minHeight: 48,
          justifyContent: open ? "initial" : "center",
          px: 2.5,
          paddingRight: "26px",
          "& .Mui-selected": {
            backgroundColor: "transparent !important",
          },
          // Check if any subitem is selected & accordion is closed, only then change bg color of main item.
          backgroundColor:
            isSubItemActive && !accordionOpen ? "#F9F7FD" : "transparent",
          borderRadius: "4px !important",
          // === Changes start here ===

          // 1. The container needs position: relative for the pseudo-element to be positioned against it
          position: "relative",

          // 2. Conditionally add styles for the ::before pseudo-element
          ...(isSubItemActive && !accordionOpen
            ? {
                // Target the ::before pseudo-element of *this* component
                "&::before": {
                  content: '""', // Required for pseudo-elements
                  position: "absolute", // Position relative to the container (due to position: 'relative' above)
                  top: "10%", // Adjust these percentages for vertical spacing
                  bottom: "10%", // (10% top space + 80% line + 10% bottom space = 100%)
                  left: 0, // Position at the left edge
                  width: "2px", // The thickness of your line
                  backgroundColor: "#703DDE", // The color of your line
                  // Optional: Ensure it doesn't interfere with content layout or events
                  borderRadius: "4px",
                  pointerEvents: "none",
                },
              }
            : {}), // If the condition is false, spread an empty object, effectively adding no ::before styles

          // 3. Remove the old borderLeft style
          // borderLeft: isSubItemActive && !accordionOpen ? "2px solid #703DDE" : "none", // REMOVE THIS LINE

          // === Changes end here ===
        }}
        onClick={() => {
          if (open && openSubItemsAccordionWhenClosed !== item.text) {
            handleItemClick(item.text, item.subItems);
          }
          setSearch("");
          if (handleClearOpenedSubItemsAccordion) {
            handleClearOpenedSubItemsAccordion();
          }
        }}
        selected={selectedItem === item.text && !childSelected}
      >
        <VeBox
          // width="168px"
          width="85%"
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <VeBox
            width={open ? "28px" : undefined}
            display="flex"
            alignItems="center"
          >
            <ListItemIcon
              sx={{
                minWidth: 0,
                mr: open ? 3 : "auto",
                justifyContent: "center",
              }}
            >
              {/* Check if any subitem is selected & accordion is closed, only then make main item icon color to purple */}
              {item?.isDisabled
                ? item?.disabledIcon
                : isSubItemActive && !accordionOpen
                  ? item.selectedIcon
                  : item.icon}
            </ListItemIcon>
          </VeBox>
          {open && (
            <ListItemText
              sx={{
                "& .Mui-selected": {
                  backgroundColor: "transparent !important",
                },
              }}
            >
              <VeTypography
                variant={
                  item?.isDisabled
                    ? "label2Disabled"
                    : isSubItemActive && !accordionOpen
                      ? "label2StrongBrand"
                      : "label2Main"
                }
              >
                {Capitalize(item.text)}
              </VeTypography>
            </ListItemText>
          )}
        </VeBox>

        {item?.subItems && open && (
          <IconButton
            edge="end"
            size="small"
            sx={{
              "&.MuiIconButton-root": {
                padding: 0,
              },
            }}
            className="chevron-icon-btn"
          >
            <FontAwesomeIcon
              icon={
                selectedItem === item.text ||
                openSubItemsAccordionWhenClosed === item.text
                  ? faChevronDown
                  : faChevronRight
              }
              className="fontAwesome-icon"
              size="xs"
              height={
                selectedItem === item.text ||
                openSubItemsAccordionWhenClosed === item.text
                  ? "5px"
                  : "8px"
              }
              width={
                selectedItem === item.text ||
                openSubItemsAccordionWhenClosed === item.text
                  ? "8px"
                  : "5px"
              }
              bounce={false}
              style={{
                color: "#8F9295",
                width: "7px !important",
                height: "4px !important",
              }}
            />
          </IconButton>
        )}
      </ListItemButton>
      <Collapse
        sx={{
          "& .Mui-selected": {
            backgroundColor: "transparent",
          },
        }}
        in={
          selectedItem === item.text ||
          isSearchMatch ||
          openSubItemsAccordionWhenClosed === item.text ||
          isSubItemActive
        }
        timeout="auto"
        unmountOnExit
        onEntered={() => setAccordionOpen(true)}
        onExited={() => setAccordionOpen(false)}
      >
        <List component="div" disablePadding>
          {item.subItems !== undefined &&
            open &&
            item.subItems
              ?.filter((subItem: any) => permissionCheckFn(subItem?.permitName))
              ?.map((item: any) => (
                <Link href={item.path || "#"}>
                  <ListItem
                    key={item.text}
                    disablePadding
                    sx={{
                      display: "block",
                      // borderLeft: "1px solid #D5DBE1",
                      marginLeft: "28px",
                      "& .MuiListItemButton-root": {
                        padding: "10px 36px 10px 15px",
                        // padding: "10px 36px 10px 44px",
                      },
                    }}
                  >
                    <ListItemButton
                      sx={{
                        minHeight: 48,
                        justifyContent: open ? "initial" : "center",
                        px: 2.5,
                        backgroundColor:
                          pathName === item.path ? "#F9F7FD" : "transparent",
                        borderLeft:
                          pathName === item.path
                            ? "none"
                            : "0.5px solid #D5DBE1",
                        borderRadius: pathName === item.path ? "4px" : "0",

                        position: "relative",

                        // 2. Conditionally add styles for the ::before pseudo-element
                        ...(pathName === item.path
                          ? {
                              // Target the ::before pseudo-element of *this* component
                              "&::before": {
                                content: '""', // Required for pseudo-elements
                                position: "absolute", // Position relative to the container (due to position: 'relative' above)
                                top: "10%", // Adjust these percentages for vertical spacing
                                bottom: "10%", // (10% top space + 80% line + 10% bottom space = 100%)
                                left: 0, // Position at the left edge
                                width: "2px", // The thickness of your line
                                backgroundColor: "#703DDE", // The color of your line
                                // Optional: Ensure it doesn't interfere with content layout or events
                                borderRadius: "4px",
                                pointerEvents: "none",
                              },
                            }
                          : {}),
                      }}
                      onClick={() => {
                        setChildSelected(item?.text);
                        setSearch("");
                        localStorage.setItem("selectedSubItem", item?.text);
                        if (handleClearOpenedSubItemsAccordion) {
                          handleClearOpenedSubItemsAccordion();
                        }
                        if (handleDrawerClose) {
                          handleDrawerClose();
                        }
                      }}
                      selected={pathName === item.path}
                    >
                      <VeTypography
                        variant={
                          pathName === item.path
                            ? "label2StrongBrand"
                            : "label2Main"
                        }
                      >
                        {Capitalize(item.text)}
                      </VeTypography>
                    </ListItemButton>
                  </ListItem>
                </Link>
              ))}
        </List>
      </Collapse>
    </ListItem>
  );
};
